import { jsPDF } from 'jspdf';
import { generateSpecification, HDF_BACK } from '@/lib/specification';
import { installCyrillicFont } from '@/lib/pdfFont';

// Система 32: расстояние от краёв 37мм, полкодержатели 32мм, минификс D15, шкант 8мм.
// Координаты отверстий задаются относительно детали с осями:
//   X — вдоль L (длина), Y — вдоль W (ширина), Z — в торец.
// face: 'front' (пласть лицевая) | 'top' / 'bottom' / 'left' / 'right' (торцы).

const OFFSET_END = 50;      // отступ от торца при corpus-присадке
const OFFSET_FRONT = 37;    // отступ от лицевой кромки
const OFFSET_BACK = 37;     // отступ от задней кромки

// Типы отверстий с диаметром, цветом и меткой
const HOLE_TYPES = {
  minifix_body:   { d: 15, color: [200, 80, 50],  label: 'M' },  // чаша минификса D15 в пласти
  minifix_end:    { d: 5,  color: [100, 120, 200], label: 'm' },  // ответное D5 в торце
  dowel:          { d: 8,  color: [80, 130, 80],  label: 'D' },   // шкант D8
  shelf_pin:      { d: 5,  color: [160, 100, 180], label: 'p' },  // полкодержатель D5
  hinge_cup:      { d: 35, color: [180, 60, 60],  label: 'H' },   // чаша петли D35
  handle:         { d: 4,  color: [40, 40, 40],   label: 'R' },   // отверстие ручки
};

function genHolesForPart(p) {
  // Соглашение: L — длинная сторона (высота детали на схеме), W — короткая.
  // Оси: x вдоль W (по горизонтали), y вдоль L (по вертикали).
  const holes = [];
  const category = p.category || classifyPart(p);
  const L = p.L, W = p.W;

  switch (category) {
    case 'side_wall': {
      // Стенка корпуса: y вдоль высоты, x вдоль глубины. Минификсы для крыши (y=OFFSET_END) и дна (y=L-OFFSET_END), по 2 на торец.
      holes.push({ type: 'minifix_body', x: OFFSET_FRONT,     y: OFFSET_END,     note: 'под крышу' });
      holes.push({ type: 'minifix_body', x: W - OFFSET_BACK,  y: OFFSET_END,     note: 'под крышу' });
      holes.push({ type: 'minifix_body', x: OFFSET_FRONT,     y: L - OFFSET_END, note: 'под дно' });
      holes.push({ type: 'minifix_body', x: W - OFFSET_BACK,  y: L - OFFSET_END, note: 'под дно' });
      // Шкант в верхнем и нижнем торце (в пласть не видно, но для схемы обозначим)
      holes.push({ type: 'dowel', x: W / 2, y: 8,     note: 'торец сверху' });
      holes.push({ type: 'dowel', x: W / 2, y: L - 8, note: 'торец снизу' });
      // Полкодержатели: 6 позиций вдоль высоты на передней и задней линиях
      const startY = 200, endY = L - 200;
      const steps = 6;
      for (let i = 0; i < steps; i++) {
        const yy = startY + (endY - startY) * i / (steps - 1);
        holes.push({ type: 'shelf_pin', x: OFFSET_FRONT,    y: Math.round(yy) });
        holes.push({ type: 'shelf_pin', x: W - OFFSET_BACK, y: Math.round(yy) });
      }
      break;
    }
    case 'top_bottom': {
      // Крыша/дно: y вдоль длины корпуса (=L), x вдоль глубины (=W). Минификс в торец к стенкам.
      holes.push({ type: 'minifix_end', x: OFFSET_FRONT,    y: OFFSET_END,     note: 'к стенке' });
      holes.push({ type: 'minifix_end', x: W - OFFSET_BACK, y: OFFSET_END });
      holes.push({ type: 'minifix_end', x: OFFSET_FRONT,    y: L - OFFSET_END });
      holes.push({ type: 'minifix_end', x: W - OFFSET_BACK, y: L - OFFSET_END });
      holes.push({ type: 'dowel', x: W / 2, y: 8 });
      holes.push({ type: 'dowel', x: W / 2, y: L - 8 });
      break;
    }
    case 'shelf': {
      // Полка: y вдоль длины, x вдоль глубины. 4 минификса в торцах.
      holes.push({ type: 'minifix_end', x: OFFSET_FRONT,    y: OFFSET_END });
      holes.push({ type: 'minifix_end', x: W - OFFSET_BACK, y: OFFSET_END });
      holes.push({ type: 'minifix_end', x: OFFSET_FRONT,    y: L - OFFSET_END });
      holes.push({ type: 'minifix_end', x: W - OFFSET_BACK, y: L - OFFSET_END });
      break;
    }
    case 'door': {
      // Фасад: L=высота, W=ширина. Петли вдоль левой кромки (x=22). Ручка вертикальная по правой кромке.
      holes.push({ type: 'hinge_cup', x: 22, y: 100 });
      holes.push({ type: 'hinge_cup', x: 22, y: L - 100 });
      holes.push({ type: 'handle', x: W - 25, y: L / 2 - 64 });
      holes.push({ type: 'handle', x: W - 25, y: L / 2 + 64 });
      break;
    }
    case 'drawer_front': {
      // Фасад ящика: L=высота, W=ширина. Ручка горизонтальная по центру.
      holes.push({ type: 'handle', x: W / 2 - 64, y: L / 2 });
      holes.push({ type: 'handle', x: W / 2 + 64, y: L / 2 });
      holes.push({ type: 'dowel', x: 20,     y: 20,     note: 'к каркасу' });
      holes.push({ type: 'dowel', x: W - 20, y: 20 });
      holes.push({ type: 'dowel', x: 20,     y: L - 20 });
      holes.push({ type: 'dowel', x: W - 20, y: L - 20 });
      break;
    }
    case 'drawer_side': {
      // Боковина ящика: L=глубина ящика, W=высота ящика. Направляющие по центру W.
      holes.push({ type: 'minifix_end', x: W / 2, y: 37,     note: 'направляющая' });
      holes.push({ type: 'minifix_end', x: W / 2, y: L - 37, note: 'направляющая' });
      holes.push({ type: 'minifix_body', x: 15,     y: 15, note: 'к фасаду' });
      holes.push({ type: 'minifix_body', x: W - 15, y: 15 });
      break;
    }
    case 'back_panel':
    default:
      break;
  }
  return holes.filter(h => h.x >= 0 && h.y >= 0 && h.x <= W && h.y <= L);
}

function classifyPart(p) {
  const n = (p.name || '').toLowerCase();
  if (n.includes('стенка лев') || n.includes('стенка прав') || n.includes('боковина ящ')) {
    if (n.includes('боковина ящ')) return 'drawer_side';
    return 'side_wall';
  }
  if (n.includes('крыша') || n.includes('дно')) return 'top_bottom';
  if (n.includes('полка')) return 'shelf';
  if (n.includes('фасад ящика')) return 'drawer_front';
  if (n.includes('фасад') || n === 'дверь' || n.startsWith('дверь')) return 'door';
  if (n.includes('задняя стенка') || n.includes('дно ящика')) return 'back_panel';
  if (n.includes('изголовье') || n.includes('столешница')) return 'top_bottom';
  return 'other';
}

// Собирает уникальные детали из спецификации и формирует присадочные карты
export function generateDrilling(modules) {
  const spec = generateSpecification(modules);
  const parts = [];
  for (const mg of spec.materials) {
    if (mg.material === HDF_BACK) continue; // ХДФ не сверлим
    for (const item of mg.items) {
      const category = classifyPart(item);
      if (category === 'back_panel' || category === 'other') continue;
      const holes = genHolesForPart({ ...item, category });
      if (holes.length === 0) continue;
      parts.push({
        ...item,
        category,
        material: mg.material,
        colorId: mg.colorId,
        holes,
      });
    }
  }
  return parts;
}

// ---------- PDF ----------

function drawPart(pdf, part, idx, total, projectName) {
  const pageW = pdf.internal.pageSize.getWidth();   // 210
  const pageH = pdf.internal.pageSize.getHeight();  // 297
  const margin = 14;
  const headerH = 26;
  const legendH = 50;
  const drawW = pageW - 2 * margin;
  const drawH = pageH - margin - headerH - legendH;

  const scale = Math.min(drawW / part.W, drawH / part.L);
  const px = margin + (drawW - part.W * scale) / 2;
  const py = margin + headerH;

  // Шапка
  pdf.setFont('Roboto', 'bold'); pdf.setFontSize(12);
  pdf.text(`${projectName} · Присадка детали`, margin, margin + 5);
  pdf.setFont('Roboto', 'normal'); pdf.setFontSize(10);
  pdf.text(`${idx + 1} / ${total}  ${part.name}`, margin, margin + 11);
  pdf.setFontSize(9);
  pdf.text(`${part.L} × ${part.W} мм · ${part.material.nameRu} · толщина ${part.material.thickness} мм · количество ${part.qty}`,
    margin, margin + 17);

  // Контур детали
  pdf.setLineWidth(0.4);
  pdf.setDrawColor(0);
  pdf.setFillColor(250, 247, 240);
  pdf.rect(px, py, part.W * scale, part.L * scale, 'FD');

  // Метки осей L/W
  pdf.setFontSize(8); pdf.setTextColor(120);
  pdf.text('W', px + part.W * scale / 2, py - 2, { align: 'center' });
  pdf.text('L', px - 4, py + part.L * scale / 2, { angle: 90 });
  pdf.text(`${part.W}`, px + part.W * scale / 2, py + part.L * scale + 6, { align: 'center' });
  pdf.text(`${part.L}`, px + part.W * scale + 4, py + part.L * scale / 2);
  pdf.setTextColor(0);

  // Отверстия
  part.holes.forEach((h) => {
    const t = HOLE_TYPES[h.type];
    if (!t) return;
    const hx = px + h.x * scale;
    const hy = py + h.y * scale;
    pdf.setDrawColor(...t.color);
    pdf.setFillColor(255, 255, 255);
    pdf.setLineWidth(0.3);
    const r = Math.max(0.8, (t.d * scale) / 2);
    pdf.circle(hx, hy, r, 'FD');
    // Крестик центра
    pdf.setLineWidth(0.15);
    pdf.line(hx - r - 0.4, hy, hx + r + 0.4, hy);
    pdf.line(hx, hy - r - 0.4, hx, hy + r + 0.4);
    // Подпись координаты
    pdf.setFontSize(6.5);
    pdf.setTextColor(...t.color);
    pdf.text(`${t.label} ${Math.round(h.y)};${Math.round(h.x)}`, hx + r + 1, hy - 0.5);
  });
  pdf.setTextColor(0);

  // Легенда
  const ly = py + part.L * scale + 14;
  pdf.setFont('Roboto', 'bold'); pdf.setFontSize(9);
  pdf.text('Условные обозначения', margin, ly);
  pdf.setFont('Roboto', 'normal'); pdf.setFontSize(8);
  const legend = [
    { type: 'minifix_body', text: 'M — чаша минификса D15 в пласти' },
    { type: 'minifix_end',  text: 'm — ответное минификса D5 в торце' },
    { type: 'dowel',        text: 'D — шкант D8 в торце' },
    { type: 'shelf_pin',    text: 'p — полкодержатель D5 (система 32)' },
    { type: 'hinge_cup',    text: 'H — чаша петли D35 в пласти' },
    { type: 'handle',       text: 'R — отверстие под ручку' },
  ];
  // Показываем только те, что реально есть на детали
  const used = new Set(part.holes.map(h => h.type));
  let ly0 = ly + 5;
  legend.forEach(l => {
    if (!used.has(l.type)) return;
    const t = HOLE_TYPES[l.type];
    pdf.setDrawColor(...t.color);
    pdf.setFillColor(255, 255, 255);
    pdf.circle(margin + 3, ly0 - 1, 1.4, 'FD');
    pdf.setTextColor(0);
    pdf.text(l.text, margin + 8, ly0);
    ly0 += 5;
  });

  // Счётчик отверстий
  const counts = {};
  part.holes.forEach(h => { counts[h.type] = (counts[h.type] || 0) + 1; });
  let sx = pageW - margin - 60;
  pdf.setFont('Roboto', 'bold'); pdf.setFontSize(9);
  pdf.text('Отверстий на деталь:', sx, ly);
  pdf.setFont('Roboto', 'normal'); pdf.setFontSize(8);
  let sy = ly + 5;
  Object.entries(counts).forEach(([type, qty]) => {
    const t = HOLE_TYPES[type];
    pdf.text(`${t.label} × ${qty}  (D${t.d})`, sx, sy);
    sy += 5;
  });
}

export async function exportDrillingPDF(modules, { projectName = 'Проект' } = {}) {
  const parts = generateDrilling(modules);
  if (parts.length === 0) return { parts: [] };

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  await installCyrillicFont(pdf);

  // Титул
  pdf.setFont('Roboto', 'bold'); pdf.setFontSize(18);
  pdf.text(projectName, 14, 24);
  pdf.setFontSize(14);
  pdf.text('Карты присадки', 14, 34);
  pdf.setFont('Roboto', 'normal'); pdf.setFontSize(10);
  pdf.text(`Всего уникальных деталей: ${parts.length}`, 14, 46);
  let y = 60;
  pdf.setFont('Roboto', 'bold');
  pdf.text('№', 14, y); pdf.text('Деталь', 22, y); pdf.text('L×W, мм', 100, y);
  pdf.text('Кол-во', 140, y); pdf.text('Отверстий', 170, y);
  pdf.setFont('Roboto', 'normal');
  pdf.setLineWidth(0.2); pdf.line(14, y + 1.5, 196, y + 1.5);
  y += 6;
  parts.forEach((p, i) => {
    pdf.text(String(i + 1), 14, y);
    pdf.text(p.name, 22, y);
    pdf.text(`${p.L}×${p.W}`, 100, y);
    pdf.text(String(p.qty), 140, y);
    pdf.text(String(p.holes.length), 170, y);
    y += 5;
    if (y > 270) { pdf.addPage(); y = 20; }
  });

  parts.forEach((p, i) => {
    pdf.addPage();
    drawPart(pdf, p, i, parts.length, projectName);
  });

  const safeName = (projectName || 'drilling').replace(/[^\wа-яА-Я0-9-_ ]/gi, '').slice(0, 60);
  pdf.save(`${safeName || 'drilling'}_присадка.pdf`);
  return { parts };
}
